import { jsx as _jsx } from "react/jsx-runtime";
import { hasStockAutoLinkTextBoundaries, isAutoLinkTextInProtectedContext, } from '#pplx/frontend/composer-sdk/internal/link/autoLinkText.js';
import { $createAutoLinkNode, $createLinkNode, $isAutoLinkNode, $isLinkNode, AutoLinkNode, LinkNode, } from '@lexical/link';
import { createLinkMatcherWithRegExp, AutoLinkPlugin as LexicalAutoLinkPlugin, } from '@lexical/react/LexicalAutoLinkPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ParagraphNode, TextNode } from 'lexical';
import React from 'react';
const supportsLookbehind = (() => {
    try {
        new RegExp('(?<![-.+():%])');
        return true;
    }
    catch {
        return false;
    }
})();
// These are the recommended regular expressions from the Lexical Playground Auto Link Plugin.
// Though Safari <16.4 does not support lookbehind, so we have to remove the (?<!...) part.
// https://github.com/facebook/lexical/blob/main/packages/lexical-playground/src/plugins/AutoLinkPlugin/index.tsx
const URL_REGEX = supportsLookbehind
    ? /((https?:\/\/(www\.)?)|(www\.))[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)(?<![-.+():%])/
    : /((https?:\/\/(www\.)?)|(www\.))[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/;
const EMAIL_REGEX = /(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))/;
const MATCHERS = [
    createLinkMatcherWithRegExp(URL_REGEX, (text) => {
        return text.startsWith('http') ? text : `https://${text}`;
    }),
    createLinkMatcherWithRegExp(EMAIL_REGEX, (text) => {
        return `mailto:${text}`;
    }),
];
function getLinkTextContext(node) {
    const parent = node.getParent();
    if (!parent) {
        return null;
    }
    let startOffset = 0;
    for (const child of parent.getChildren()) {
        if (child.getKey() === node.getKey()) {
            return {
                endOffset: startOffset + node.getTextContent().length,
                startOffset,
                text: parent.getTextContent(),
            };
        }
        startOffset += child.getTextContent().length;
    }
    return null;
}
function matchesStockAutoLink(node, matchers) {
    const text = node.getTextContent();
    const match = matchers.reduce((result, matcher) => result ?? matcher(text), null);
    return match?.text === text && match.url === node.getURL() && node.getTitle() === null;
}
function replaceWithUnlinkedAutoLinkNode(node) {
    const autoLinkNode = $createAutoLinkNode(node.getURL(), {
        isUnlinked: true,
        rel: node.getRel(),
        target: node.getTarget(),
        title: node.getTitle(),
    });
    node.replace(autoLinkNode, true);
}
function replaceWithLinkNode(node) {
    const linkNode = $createLinkNode(node.getURL(), {
        rel: node.getRel(),
        target: node.getTarget(),
        title: node.getTitle(),
    });
    node.replace(linkNode, true);
}
function syncProtectedLinkState(node, matchers, suppressIn) {
    const context = getLinkTextContext(node);
    const isInProtectedContext = suppressIn?.includes('codeFence') === true && context
        ? isAutoLinkTextInProtectedContext(context)
        : false;
    if (!$isAutoLinkNode(node)) {
        if (isInProtectedContext) {
            replaceWithUnlinkedAutoLinkNode(node);
        }
        return;
    }
    const shouldUnlink = context
        ? isInProtectedContext || !hasStockAutoLinkTextBoundaries(context)
        : false;
    if (node.getIsUnlinked() !== shouldUnlink) {
        if (!shouldUnlink && !matchesStockAutoLink(node, matchers)) {
            replaceWithLinkNode(node);
            return;
        }
        node.setIsUnlinked(shouldUnlink);
    }
}
function syncSiblingLinksForTextNode(node, matchers, suppressIn) {
    const parent = node.getParent();
    if ($isLinkNode(parent)) {
        syncProtectedLinkState(parent, matchers, suppressIn);
        return;
    }
    if (parent) {
        syncChildLinks(parent, matchers, suppressIn);
    }
}
function syncChildLinks(node, matchers, suppressIn) {
    for (const child of node.getChildren()) {
        if ($isLinkNode(child)) {
            syncProtectedLinkState(child, matchers, suppressIn);
        }
    }
}
export const AutoLinkPlugin = React.memo(function AutoLinkPlugin({ matchers = MATCHERS, suppressIn, }) {
    const [editor] = useLexicalComposerContext();
    const lexicalMatchers = React.useMemo(() => [...matchers], [matchers]);
    React.useEffect(() => {
        const unregisterLinkTransform = editor.registerNodeTransform(LinkNode, (node) => syncProtectedLinkState(node, matchers, suppressIn));
        const unregisterTextTransform = editor.registerNodeTransform(TextNode, (node) => syncSiblingLinksForTextNode(node, matchers, suppressIn));
        const unregisterParagraphTransform = editor.registerNodeTransform(ParagraphNode, (node) => syncChildLinks(node, matchers, suppressIn));
        return () => {
            unregisterLinkTransform();
            unregisterTextTransform();
            unregisterParagraphTransform();
        };
    }, [editor, matchers, suppressIn]);
    return _jsx(LexicalAutoLinkPlugin, { matchers: lexicalMatchers });
});
export default AutoLinkPlugin;
//# sourceMappingURL=AutoLinkPlugin.js.map